import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UsersListLayer = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [plans, setPlans] = useState([]);
  const [showPerPage, setShowPerPage] = useState(10);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [userToChangeRole, setUserToChangeRole] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [userRatings, setUserRatings] = useState({});
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [blockLoading, setBlockLoading] = useState({});

  // Generate month options
  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Fetch plans for subscription filter
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.plans.getAll();
        if (response.success) {
          setPlans(response.data);
        } else {
          console.error("Failed to fetch plans:", response.message);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchPlans();
  }, []);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await api.users.getAll();
        if (response.success) {
          setUsers(response.data);
          fetchUserRatings(response.data);
        } else {
          toast.error(response.message || "Failed to fetch users");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("An error occurred while fetching users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fetch ratings for all users
  const fetchUserRatings = async (usersList) => {
    try {
      setLoadingRatings(true);
      const ratingsPromises = usersList.map((user) =>
        api.feedback.getUserRating(user.id)
      );
      const ratingsResponses = await Promise.allSettled(ratingsPromises);

      const ratingsData = {};
      usersList.forEach((user, index) => {
        const result = ratingsResponses[index];
        if (result.status === "fulfilled" && result.value.success) {
          ratingsData[user.id] = result.value.data;
        } else {
          ratingsData[user.id] = { avgRating: 0, totalFeedback: 0 };
        }
      });

      setUserRatings(ratingsData);
    } catch (error) {
      console.error("Error fetching user ratings:", error);
    } finally {
      setLoadingRatings(false);
    }
  };

  // Render star rating
  const renderStarRating = (userId) => {
    const rating = userRatings[userId]?.avgRating || 0;
    const totalFeedback = userRatings[userId]?.totalFeedback || 0;

    if (loadingRatings) {
      return <small className="text-muted">Loading...</small>;
    }

    if (totalFeedback === 0) {
      return <small className="text-muted">No ratings</small>;
    }

    // Round to nearest 0.5
    const roundedRating = Math.round(rating * 2) / 2;

    return (
      <div className="d-flex align-items-center">
        <div className="text-warning me-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Icon
              key={star}
              icon={
                star <= roundedRating
                  ? "ph:star-fill"
                  : star <= roundedRating + 0.5
                  ? "ph:star-half-fill"
                  : "ph:star"
              }
              className="icon-sm"
            />
          ))}
        </div>
        <small className="text-muted">({totalFeedback})</small>
      </div>
    );
  };

  // Toggle user block status
  const handleToggleBlockStatus = async (user) => {
    // Don't allow blocking admin users
    if (user.role === "ADMIN") {
      toast.error("Admin users cannot be blocked");
      return;
    }

    // Set loading state for this specific user
    setBlockLoading((prev) => ({ ...prev, [user.id]: true }));

    try {
      const response = await api.users.toggleUserBlock(
        user.id,
        !user.isBlocked
      );

      if (response.success) {
        // Update the user in the local state
        setUsers(
          users.map((u) =>
            u.id === user.id ? { ...u, isBlocked: response.data.isBlocked } : u
          )
        );

        toast.success(
          response.message ||
            `User ${
              response.data.isBlocked ? "blocked" : "unblocked"
            } successfully`
        );
      } else {
        toast.error(response.message || "Failed to update user block status");
      }
    } catch (error) {
      console.error("Error toggling user block status:", error);
      toast.error("An error occurred while updating user block status");
    } finally {
      // Clear loading state for this user
      setBlockLoading((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  // Handle checkbox selection
  const toggleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Handle select all checkbox
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((user) => user.id));
    }
    setSelectAll(!selectAll);
  };

  // Filter users based on search term, status, month, and subscription
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchTerm === "" ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "Active" && user.isVerified) ||
      (statusFilter === "Inactive" && !user.isVerified) ||
      (statusFilter === "Blocked" && user.isBlocked);

    // Filter by account creation month
    const matchesMonth =
      monthFilter === "" ||
      (user.createdAt &&
        new Date(user.createdAt).getMonth() + 1 === parseInt(monthFilter));

    // Filter by subscription type
    const matchesSubscription =
      subscriptionFilter === "" ||
      user.subscription?.plan?.name === subscriptionFilter;

    return (
      matchesSearch && matchesStatus && matchesMonth && matchesSubscription
    );
  });

  // Display only the first n users based on showPerPage
  const displayedUsers = filteredUsers.slice(0, showPerPage);

  // Handle user deletion
  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const deleteUser = async () => {
    try {
      const response = await api.users.delete(userToDelete.id);
      if (response.success) {
        setUsers(users.filter((user) => user.id !== userToDelete.id));
        toast.success("User deleted successfully");
      } else {
        toast.error(response.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("An error occurred while deleting the user");
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  // Handle user role change
  const openRoleModal = (user) => {
    setUserToChangeRole(user);
    setNewRole(user.role);
    setShowRoleModal(true);
  };

  const changeUserRole = async () => {
    try {
      const response = await api.users.changeRole(userToChangeRole.id, newRole);
      if (response.success) {
        setUsers(
          users.map((user) =>
            user.id === userToChangeRole.id ? { ...user, role: newRole } : user
          )
        );
        toast.success("User role changed successfully");
      } else {
        toast.error(response.message || "Failed to change user role");
      }
    } catch (error) {
      console.error("Error changing user role:", error);
      toast.error("An error occurred while changing the user role");
    } finally {
      setShowRoleModal(false);
      setUserToChangeRole(null);
    }
  };

  // Format date to display in a more readable format
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get subscription info to display
  const getSubscriptionInfo = (user) => {
    if (!user.subscription) return "No Subscription";

    if (!user.subscription.isActive) return "Inactive";

    return (
      user.subscription.plan?.displayName ||
      user.subscription.plan?.name ||
      "Unknown"
    );
  };

  // Get subscription badge color
  const getSubscriptionBadgeClass = (user) => {
    if (!user.subscription || !user.subscription.isActive) {
      return "bg-neutral-200 text-neutral-700";
    }

    const planName = user.subscription.plan?.name?.toUpperCase();

    switch (planName) {
      case "FREE":
        return "bg-info-100 text-info-700";
      case "BASIC":
        return "bg-success-100 text-success-700";
      case "PRO":
        return "bg-primary-100 text-primary-700";
      case "ENTERPRISE":
        return "bg-warning-100 text-warning-700";
      default:
        return "bg-neutral-200 text-neutral-700";
    }
  };

  if (loading) {
    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 d-flex justify-content-center align-items-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      <ToastContainer />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete {userToDelete?.name}?</p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={deleteUser}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change User Role</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRoleModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Change role for {userToChangeRole?.name}</p>
                <select
                  className="form-select"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                  <option value="INSTRUCTOR">Instructor</option>
                </select>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRoleModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={changeUserRole}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={showPerPage}
            onChange={(e) => setShowPerPage(Number(e.target.value))}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <div className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="search"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </div>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Blocked">Blocked</option>
          </select>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            <option value="">All Months</option>
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={subscriptionFilter}
            onChange={(e) => setSubscriptionFilter(e.target.value)}
          >
            <option value="">All Subscriptions</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.name}>
                {plan.displayName}
              </option>
            ))}
            <option value="NO_SUBSCRIPTION">No Subscription</option>
          </select>
        </div>
        <Link
          to="/add-user"
          className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
        >
          <Icon
            icon="ic:baseline-plus"
            className="icon text-xl line-height-1"
          />
          Add New User
        </Link>
      </div>
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">
                  <div className="d-flex align-items-center gap-10">
                    <div className="form-check style-check d-flex align-items-center">
                      <input
                        className="form-check-input radius-4 border input-form-dark"
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                        id="selectAll"
                      />
                    </div>
                    ID
                  </div>
                </th>
                <th scope="col">Join Date</th>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Subscription</th>
                <th scope="col">Rating</th>
                <th scope="col" className="text-center">
                  Status
                </th>
                <th scope="col" className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length > 0 ? (
                displayedUsers.map((user, index) => (
                  <tr key={user.id}>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <div className="form-check style-check d-flex align-items-center">
                          <input
                            className="form-check-input radius-4 border border-neutral-400"
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleSelectUser(user.id)}
                          />
                        </div>
                        {index + 1}
                      </div>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="w-40-px h-40-px rounded-circle bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center me-12 overflow-hidden">
                          {user.avatar ? (
                            <img
                              src={user.avatar}
                              alt={user.name}
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : (
                            <span>
                              {user.name?.charAt(0)?.toUpperCase() ||
                                user.firstName?.charAt(0)?.toUpperCase() ||
                                "U"}
                            </span>
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {user.name || `${user.firstName} ${user.lastName}`}
                          </span>
                          {user.isBlocked && (
                            <div
                              className="badge bg-danger text-white mt-1"
                              style={{ fontSize: "9px" }}
                            >
                              Blocked
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {user.email}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`fw-medium ${
                          user.role === "ADMIN"
                            ? "text-primary-600"
                            : user.role === "INSTRUCTOR"
                            ? "text-success-600"
                            : "text-secondary-light"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${getSubscriptionBadgeClass(
                          user
                        )} px-12 py-4 radius-4 fw-medium text-sm`}
                      >
                        {getSubscriptionInfo(user)}
                      </span>
                    </td>
                    <td>{renderStarRating(user.id)}</td>
                    <td className="text-center">
                      <span
                        className={`${
                          user.isBlocked
                            ? "bg-danger-focus text-danger-600 border border-danger-main"
                            : user.isVerified
                            ? "bg-success-focus text-success-600 border border-success-main"
                            : "bg-danger-focus text-danger-600 border border-danger-main"
                        } px-24 py-4 radius-4 fw-medium text-sm`}
                      >
                        {user.isBlocked
                          ? "Blocked"
                          : user.isVerified
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          type="button"
                          className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="View User Details"
                          onClick={() =>
                            (window.location.href = `/view-profile?id=${user.id}`)
                          }
                        >
                          <Icon
                            icon="majesticons:eye-line"
                            className="icon text-xl"
                          />
                        </button>
                        <button
                          type="button"
                          className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Change User Role"
                          onClick={() => openRoleModal(user)}
                        >
                          <Icon icon="carbon:user-role" className="menu-icon" />
                        </button>
                        {user.role !== "ADMIN" && (
                          <button
                            type="button"
                            className={`fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle ${
                              user.isBlocked
                                ? "bg-success-focus text-success-600 bg-hover-success-200"
                                : "bg-warning-focus text-warning-600 bg-hover-warning-200"
                            }`}
                            title={
                              user.isBlocked ? "Unblock User" : "Block User"
                            }
                            onClick={() => handleToggleBlockStatus(user)}
                            disabled={blockLoading[user.id]}
                          >
                            {blockLoading[user.id] ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <Icon
                                icon={
                                  user.isBlocked ? "mdi:lock-open" : "mdi:lock"
                                }
                                className="menu-icon"
                              />
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Delete User"
                          onClick={() => confirmDeleteUser(user)}
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination could be added here */}
      </div>
    </div>
  );
};

export default UsersListLayer;
